import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { AppUpdate, AppUpdateAvailability, AppUpdateResultCode, FlexibleUpdateInstallStatus } from "@capawesome/capacitor-app-update";
import { AlertController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { KeyValueDbDao } from "../dao/db/keyValue.db.dao";
import { DateHelper } from "../helpers/date.helper";
import { DeviceHelper, PlatformName } from "../helpers/device.helper";
import { isNullOrEmpty } from "../helpers/null.helper";
import { DateProvider } from "../interfaces/dateProvider";
import { IAppConfigurationProvider } from "../interfaces/IAppConfigurationProvider";
import { KeyValue } from "../models/keyValue.model";
import { LoggerService } from "./logs/logger.service";

@Injectable({
    providedIn: "root",
})
export class AppUpdateService {

    constructor(private logger: LoggerService,
                private keyValueDbDao: KeyValueDbDao,
                private dateProvider: DateProvider,
                private deviceHelper: DeviceHelper,
                private translateService: TranslateService,
                private appConfigurationProvider: IAppConfigurationProvider,
                private alertController: AlertController) {
    }

    public async askForUpdateIfNeeded() {
        try {
            if (!this.deviceHelper.isRunningOnDevice()) {
                return;
            }

            let currentVersion = await this.getCurrentAppVersion();
            let availableAppVersion = await this.getAvailableAppVersion();

            if (availableAppVersion > currentVersion) {
                let result = await AppUpdate.getAppUpdateInfo();
                if (result.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE && await this.shouldAskForUpdate()) {
                    if (this.deviceHelper.getPlatform() == PlatformName.IOS) {
                        // Sur iOS : seulement rediriger vers l'App Store
                        await this.showIOSUpdateAlert();
                    } else {
                        // Sur Android : mise à jour flexible
                        let result = await this.startFlexibleUpdate();
                        if (result.code == AppUpdateResultCode.CANCELED) {
                            await this.keyValueDbDao.save(new KeyValue("lastUpdateDeclined", DateHelper.tryToISO(this.dateProvider.now())));
                        }
                    }
                }
            }
        } catch (error) {
            this.logger.error(this.constructor.name, "Erreur lors de la vérification de mise à jour", error);
        }
    }

    private async completeFlexibleUpdate() {
        await AppUpdate.completeFlexibleUpdate();
    };

    private async getAvailableAppVersion() {
        const result = await AppUpdate.getAppUpdateInfo();
        if (Capacitor.getPlatform() === "android") {
            return result.availableVersionCode;
        } else {
            return result.availableVersionName;
        }
    };

    private async getCurrentAppVersion() {
        const result = await AppUpdate.getAppUpdateInfo();
        if (Capacitor.getPlatform() === "android") {
            return result.currentVersionCode;
        } else {
            return result.currentVersionName;
        }
    };

    private async openAppStore() {
        await AppUpdate.openAppStore({ appId: this.appConfigurationProvider.getAppleAppId() });
    };

    private async shouldAskForUpdate(): Promise<boolean> {
        const lastDeclined = await this.keyValueDbDao.get("lastUpdateDeclined");
        if (lastDeclined && !isNullOrEmpty(lastDeclined.value)) {
            const daysSinceDeclined = this.dateProvider.now().diff(DateHelper.tryFromISO(lastDeclined.value), "days");
            return daysSinceDeclined.days >= 1; // Attendre 1 jour avant de redemander
        }

        return true;
    }

    private async showIOSUpdateAlert(): Promise<void> {
        const alert = await this.alertController.create({
            header: this.translateService.instant("IONIC_Update_available"),
            message: this.translateService.instant("IONIC_Ask_download_update"),
            buttons: [
                {
                    text: this.translateService.instant("IONIC_LaterOn"),
                    role: "cancel",
                    handler: () => {
                        void this.keyValueDbDao.save(new KeyValue("lastUpdateDeclined", DateHelper.tryToISO(this.dateProvider.now())));
                    },
                },
                {
                    text: this.translateService.instant("IONIC_Update"),
                    handler: async () => {
                        await this.openAppStore();
                    },
                },
            ],
        });

        await alert.present();
    }

    private async startFlexibleUpdate() {
        const result = await AppUpdate.getAppUpdateInfo();
        if (result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
            return { code: AppUpdateResultCode.NOT_AVAILABLE };
        }

        if (result.flexibleUpdateAllowed) {
            await AppUpdate.removeAllListeners();
            await AppUpdate.addListener("onFlexibleUpdateStateChange", async state => {
                this.logger.debug(this.constructor.name, "onFlexibleUpdateStateChange", state);

                if (state.installStatus == FlexibleUpdateInstallStatus.DOWNLOADED) {
                    const alert = await this.alertController.create({
                        header: this.translateService.instant("IONIC_Update_ready"),
                        message: this.translateService.instant("IONIC_Ask_install_update"),
                        buttons: [
                            {
                                text: this.translateService.instant("IONIC_No"),
                                role: "cancel",
                            }, {
                                text: this.translateService.instant("IONIC_Yes"),
                                handler: async () => {
                                    await this.completeFlexibleUpdate();
                                },
                            },
                        ],
                    });

                    await alert.present();
                }
            });
            return await AppUpdate.startFlexibleUpdate();
        }
    };

}
